import { AgentBookingModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { AppUrls } from '@/urls';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PageLoading from '@/components/App/PageLoading';
import BookingHeader from '@/components/App/Header/BookingHeader';
import HotelBookingSummary from '@/components/App/BookingComponents/HotelBookingSummary';
import { getFullName } from '@/helpers/string';
import { handleBookingNotFound } from '@/helpers/errorHandle';
import BookingCompletedDetail from '@/components/App/BookingComponents/BookingCompletedDetail';
import BookingCompletedHeaderInfo from '@/components/App/BookingComponents/BookingCompletedHeaderInfo';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { Helmet } from 'react-helmet';

type Props = {};

export default function BookingFailedPage({}: Props) {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [booking, setBooking] = React.useState<AgentBookingModel>();

  const history = useHistory();
  const { isAdmin, isBooking } = useGetUseRole();
  if (!isAdmin && !isBooking) {
    history.push(AppUrls.Page401);
    return null;
  }

  React.useEffect(() => {
    if (bookingId) {
      getBookingDetail(bookingId);
    } else {
      history.push(AppUrls.HomePage);
    }
  }, [bookingId]);

  const getBookingDetail = (bId: string) => {
    setLoading(true);
    apiSdk.BookingService.getBookingDetails({ id: bId })
      .then(res => {
        if (res) {
          setBooking(res);
        } else {
          handleBookingNotFound();
        }
      })
      .catch(() => {
        handleBookingNotFound();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return <PageLoading />;
  }

  if (!booking) {
    return null;
  }
  let { bookingContactDetail } = booking;
  const fullName = getFullName(bookingContactDetail?.firstName, bookingContactDetail?.lastName);
  return (
    <div className="mb-50">
      <Helmet>
        <title>{`Booking failed - ${booking.refNumber}`}</title>
      </Helmet>
      <BookingHeader isSticky />
      <div className="header-margin" />

      <div className="container mt-20">
        <div className="row ">
          <div className="d-flex flex-column items-center mt-60 lg:md-40 sm:mt-24 text-center">
            <div className="size-80 flex-center rounded-full bg-red-1">
              <i className="icon-close text-30 text-white" />
            </div>

            <div className="text-30 lh-1 fw-600 mt-20 text-center text-red-1">{`${fullName}, your booking is not confirmed!`}</div>
            <div className="text-15 text-light-1 mt-10">Thank you for booking with Nippon Travel Agency.</div>
            <div className="text-15 text-light-1 text-center">
              Your reservation has not been confirmed yet due to availability.
              <br /> Kindly try again with different room types, accommodations, or dates.
            </div>
          </div>
          {/* End header */}
          <BookingCompletedHeaderInfo fromPage="Failed" booking={booking} />
        </div>
        <div className="row x-gap-30 ">
          <div className="col-xl-7 col-lg-8 mt-30">
            <BookingCompletedDetail booking={booking} />
          </div>
          <div className="col-xl-5 col-lg-4 mt-30 ">
            <HotelBookingSummary booking={booking} page="failed" />
          </div>
        </div>
      </div>
    </div>
  );
}
